import React from "react";
import { LucideCheckCircle2 } from "lucide-react";
import { LuClipboardList, LuPenTool, LuRefreshCw, LuTrendingUp, LuUsers } from "react-icons/lu";
import { FiBarChart, FiEdit3, FiExternalLink, FiTarget, FiTrendingUp, FiUsers } from "react-icons/fi";

export const adCampaignHeroData = {
  backgroundImage: "/images/serviceBanners/AdCampaignsB.webp",
  whiteImage: "/images/serviceBanners/AdCampaigns.webp",
  subTitle: "Ad Campaign Management Services (Meta,Google & Beyond)",
  title: (
    <>
      High Performance <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-600">Advertising </span> System
    </>
  ),
  description:
    "Effective advertising goes beyond budget spending, focusing instead on precise audience targeting, continuous optimization, and performance driven strategies that transform campaigns into measurable business growth and sustainable ROI improvement.",
  primaryButton: {
    title: "Get Started",
    onClick: () => console.log("AI Start clicked"),
  },
  secondaryButton: {
    title: "View Our Work",
    onClick: () => console.log("AI Work clicked"),
  },
  featureData: [
    {
      icon: "/images/services/website/high_performance.webp",
      title: "Fast Ad Optimization",
      desc: "Optimized for speed and sub-second inference.",
      iconColor: "#a855f7",
    },
    {
      icon: "/images/services/website/responsive.webp",
      title: "Structured Campaign Flow",
      desc: "Cloud-agnostic deployments (AWS/GCP/Azure).",
      iconColor: "#22d3ee",
    },
    {
      icon: "/images/services/website/secure.webp",
      title: "Predictable Performance Tracking",
      desc: "Enterprise-grade model governance and privacy.",
      iconColor: "#a855f7",
    },
    {
      icon: "/images/services/website/clean_code.webp",
      title: "Scalable Media Buying",
      desc: "Clean, maintainable, and monitorable pipelines.",
      iconColor: "#34d399",
    },
  ],
};

export const adCampaignAndProcessData = {
  buildSubtitle: "AD CAMPAIGN SOLUTIONS",
  buildTitle: "Ad Campaign Solutions for Every Requirement",
  buildCards: [
    {
      title: "Custom Ad Creative Optimization",
      desc: "Build high-performing ad creatives designed to improve CTR, engagement, and conversions across platforms.",
      icon: FiEdit3,
      colorClass: "text-[#3b82f6]",
      bgClass: "bg-[#3b82f6]/10",
      borderClass: "border-[#3b82f6]/20 hover:border-[#3b82f6]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(59,130,246,0.25)]",
    },
    {
      title: "Campaign Structure Engineering",
      desc: "Develop modular campaign setups for testing, scaling, and performance control.",
      icon: FiTarget,
      colorClass: "text-[#06b6d4]",
      bgClass: "bg-[#06b6d4]/10",
      borderClass: "border-[#06b6d4]/20 hover:border-[#06b6d4]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(6,182,212,0.25)]",
    },
    {
      title: "Landing Page Conversion Alignment",
      desc: "Ensure ads connect with optimized landing experiences for higher conversion rates.",
      icon: FiExternalLink,
      colorClass: "text-[#a855f7]",
      bgClass: "bg-[#a855f7]/10",
      borderClass: "border-[#a855f7]/20 hover:border-[#a855f7]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(168,85,247,0.25)]",
    },
    {
      title: "Advanced Audience Targeting",
      desc: "Segment audiences based on intent, behavior, and demographic signals for better ROI.",
      icon: FiUsers,
      colorClass: "text-[#f59e0b]",
      bgClass: "bg-[#f59e0b]/10",
      borderClass: "border-[#f59e0b]/20 hover:border-[#f59e0b]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(245,158,11,0.25)]",
    },
    {
      title: "Performance Optimization System",
      desc: "Continuously improve CPC, CTR, ROAS, and conversion efficiency.",
      icon: FiBarChart,
      colorClass: "text-[#ec4899]",
      bgClass: "bg-[#ec4899]/10",
      borderClass: "border-[#ec4899]/20 hover:border-[#ec4899]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(236,72,153,0.25)]",
    },
    {
      title: "Enterprise Campaign Scaling",
      desc: "Manage large-scale advertising systems across multiple platforms with stability and control.",
      icon: FiTrendingUp,
      colorClass: "text-[#34d399]",
      bgClass: "bg-[#34d399]/10",
      borderClass: "border-[#34d399]/20 hover:border-[#34d399]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(52,211,153,0.25)]",
    },
  ],
  processSubtitle: "AD CAMPAIGN ENGINEERING FRAMEWORK",
  processTitle: "How We Build Scalable Campaign Systems",
  processSteps: [
    {
      step: "01",
      title: "Campaign Architecture Planning",
      description: "Design structured campaign flow including funnel stages, targeting logic, and performance goals.",
      icon: LuClipboardList,
    },
    {
      step: "02",
      title: "Agile Campaign Execution",
      description: "Run iterative campaign improvements with continuous testing of ads, audiences, and creatives.",
      icon: LuRefreshCw,
    },
    {
      step: "03",
      title: "Creative & Ad Development",
      description: "Build high-performing ad creatives optimized for engagement, CTR, and conversions.",
      icon: LuPenTool,
    },
    {
      step: "04",
      title: "Audience & Data Management",
      description: "Manage user segments using intent, behavior, and demographic signals for better targeting.",
      icon: LuUsers,
    },
    {
      step: "05",
      title: "Performance Optimization",
      description: "Continuously improve CPC, CTR, ROAS, and conversion rates using data insights.",
      icon: LuTrendingUp,
    },
    {
      step: "06",
      title: "Quality & Compliance Assurance",
      description: "Ensure stable, policy-compliant, and performance-driven ad campaign execution.",
      icon: LucideCheckCircle2,
    },
  ],
  benefitsSubtitle: "WHY INVEST IN PERFORMANCE ADVERTISING SYSTEMS?",
  benefitsTitle: "Why Businesses Choose Ad Campaign Solutions",
  benefits: [
    "Real-time measurable ROI systems",
    "Scalable customer acquisition model",
    "Data-driven decision making",
    "High conversion optimization capability",
    "Multi-platform campaign control",
    "Continuous optimization cycles",
    "Sustainable revenue growth system",
  ],
  logoImage: "/images/serviceBanners/AdCampaignBanner.webp",
  whiteLogoImage: "/images/serviceBanners/AdCampaignW.webp",
};

export const googleAdsAboutData = [
  {
    title: "Google Ads Management Services for Measurable Business Growth",
    content: (
      <>
        Successful advertising is not defined by impressions alone. It is measured by how effectively campaigns generate leads, sales, and long-term customer value. Professional{" "}
        <b className="text-text-primary font-semibold">Google Ads Management Services</b> help businesses connect with high-intent audiences who are actively searching for products
        and services. Many companies struggle with rising acquisition costs because campaigns lack strategic direction and optimization. At{" "}
        <b className="text-text-primary font-semibold">Tycho Technologies</b>, advertising systems are built around business objectives, audience behavior, and performance insights
        to create campaigns focused on sustainable growth.
      </>
    ),
  },
  {
    title: "Meta Ads Management Services for Better Audience Engagement",
    content: (
      <>
        Modern consumers interact with brands across multiple digital platforms before making purchasing decisions. Effective{" "}
        <b className="text-text-primary font-semibold">Meta Ads Management Services</b> help businesses reach potential customers through relevant messaging, creative storytelling,
        and precise targeting strategies. Organizations that invest in audience-focused advertising often experience stronger engagement and improved conversion opportunities.
        Tycho Technologies develops platform-specific advertising strategies that help brands strengthen visibility, improve customer interactions, and create meaningful
        connections with their target market.
      </>
    ),
  },
  {
    title: "Ad Campaign Management Services That Improve Marketing Efficiency",
    content: (
      <>
        Managing successful campaigns requires continuous monitoring, optimization, and data analysis. Reliable{" "}
        <b className="text-text-primary font-semibold">Ad Campaign Management Services</b> help businesses improve advertising performance by identifying opportunities for better
        targeting, messaging, and budget allocation. Companies investing in <b className="text-text-primary font-semibold">Paid Advertising Services</b> often achieve stronger
        marketing outcomes because campaigns are refined using real user behavior and performance metrics. A structured campaign management approach helps improve lead quality
        while supporting consistent business growth.
      </>
    ),
  },
  {
    title: "Why Businesses Choose a Digital Advertising Agency India",
    content: (
      <>
        The demand for expert advertising management continues to grow as businesses seek predictable and measurable marketing outcomes. Companies working with a trusted{" "}
        <b className="text-text-primary font-semibold">Digital Advertising Agency India</b> often focus on improving customer acquisition, increasing conversion opportunities, and
        maximizing advertising efficiency. Businesses partnering with <b className="text-text-primary font-semibold">Tycho Technologies</b> gain access to customized advertising
        frameworks designed around measurable business goals and market opportunities. A data-focused advertising strategy helps organizations strengthen brand visibility, improve
        campaign effectiveness, and achieve long-term revenue growth.
      </>
    ),
  },
];

export const googleAdsFaqs = [
  {
    q: "How do ad campaign management services improve advertising performance?",
    a: "Ad campaign management services help businesses optimize targeting, ad creatives, bidding strategies, and budget allocation. This improves campaign efficiency, reduces wasted spend, and increases conversions across advertising platforms.",
  },
  {
    q: "What platforms are commonly included in professional ad campaign management?",
    a: "Most ad campaign management services cover platforms such as Google Ads, Meta Ads (Facebook & Instagram), YouTube Ads, LinkedIn Ads, display networks, and other digital advertising channels based on business objectives.",
  },
  {
    q: "How often should advertising campaigns be optimized for better results?",
    a: "Advertising campaigns should be monitored and optimized regularly. Continuous testing of audiences, creatives, keywords, and bidding strategies helps improve click-through rates, conversion rates, and overall return on ad spend.",
  },
  {
    q: "How does Tycho Technologies create high-performing advertising campaigns?",
    a: "Tycho Technologies builds structured advertising systems using audience research, campaign architecture planning, creative optimization, conversion-focused landing page alignment, and ongoing performance analysis to maximize campaign results.",
  },
  {
    q: "Why choose Tycho Technologies for ad campaign management services?",
    a: "Tycho Technologies combines data-driven decision making, advanced audience targeting, performance optimization, and scalable media buying strategies to help businesses improve ROI, acquire qualified customers, and achieve sustainable revenue growth.",
  },
];
